import React, { useState } from 'react';
import { Send, MessageCircle, User, AlertCircle, Pencil, Trash2, Check, X, Loader2 } from 'lucide-react';
import { formatDateTime } from '../../utils/dateUtils';
import { useAuth } from '../../contexts/AuthContext';
import { updateComment, deleteComment } from '../../services/api';
import { toast } from 'sonner';

export function CommentsSection({ comments = [], onAddComment, onCommentsChanged }) {
  const [newComment, setNewComment] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [busyId, setBusyId] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.user_type === 0 || user?.user_type === 5;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    onAddComment(newComment);
    setNewComment('');
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditText(c.comment || c.text || '');
  };
  const cancelEdit = () => { setEditingId(null); setEditText(''); };

  const saveEdit = async (id) => {
    if (!editText.trim()) { toast.error('Comment cannot be empty'); return; }
    setBusyId(id);
    try {
      await updateComment(id, editText);
      toast.success('Comment updated');
      setEditingId(null);
      setEditText('');
      onCommentsChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update comment');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this comment?')) return;
    setBusyId(id);
    try {
      await deleteComment(id);
      toast.success('Comment removed');
      onCommentsChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete comment');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-white border rounded-lg p-4" data-testid="quote-comments-section">
      <h3 className="font-semibold mb-3 flex items-center gap-2">
        <MessageCircle size={18} />
        Comments
        <span className="text-xs text-slate-500 font-normal">(Internal Only - Not visible to clients)</span>
      </h3>

      {comments.length === 0 ? (
        <div className="text-slate-500 text-sm flex items-center gap-2 py-4">
          <AlertCircle size={16} />
          <span>There is no comment yet.</span>
        </div>
      ) : (
        <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
          {comments.map((comment, idx) => {
            const canModify = isAdmin || String(comment.added_by_user_id) === String(user?.id);
            const isEditing = editingId === comment.id;
            return (
              <div key={comment.id || comment.created_at || idx} className="bg-slate-50 p-3 rounded group" data-testid={`quote-comment-${comment.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <User size={14} className="text-slate-400" />
                  <span className="text-sm font-medium text-slate-700">{comment.user_name || comment.added_by_name || 'Unknown User'}</span>
                  <span className="text-xs text-slate-400">{formatDateTime(comment.created_at)}</span>
                  {comment.updated_at && <span className="text-xs italic text-slate-400">(edited)</span>}
                  {canModify && !isEditing && (
                    <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                      <button
                        type="button"
                        onClick={() => startEdit(comment)}
                        className="text-slate-400 hover:text-primary p-1"
                        title="Edit"
                        data-testid={`quote-comment-edit-${comment.id}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(comment.id)}
                        className="text-slate-400 hover:text-red-600 p-1"
                        title="Delete"
                        data-testid={`quote-comment-delete-${comment.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
                {isEditing ? (
                  <div className="flex gap-2 items-start">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={2}
                      className="flex-1 px-2 py-1 border border-slate-300 rounded text-sm"
                      data-testid={`quote-comment-edit-input-${comment.id}`}
                    />
                    <button
                      type="button"
                      onClick={() => saveEdit(comment.id)}
                      disabled={busyId === comment.id}
                      className="px-2 py-1 bg-primary text-white rounded text-xs flex items-center gap-1 hover:bg-primary-hover"
                      data-testid={`quote-comment-edit-save-${comment.id}`}
                    >
                      {busyId === comment.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className="px-2 py-1 border border-slate-300 rounded text-xs flex items-center gap-1 hover:bg-slate-50"
                      data-testid={`quote-comment-edit-cancel-${comment.id}`}
                    >
                      <X size={12} /> Cancel
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-slate-600 whitespace-pre-wrap">{comment.comment}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Add a comment..."
          className="flex-1 px-3 py-2 border border-slate-300 rounded text-sm"
          data-testid="quote-comment-input"
        />
        <button type="submit" className="px-4 py-2 bg-primary text-white rounded text-sm hover:bg-primary-hover flex items-center gap-1" data-testid="quote-comment-submit">
          <Send size={14} />
          Add
        </button>
      </form>
    </div>
  );
}
